/** Calendar (WS6 routes): approved unscheduled renders on the left, a Budapest week grid with lanes a1–a4 on the
 * right. Placing is button-driven: select render → account + day + time → Check (every violation shown) →
 * Schedule. Posts can be moved, captioned, unscheduled, ordered on GeeLark (with a watched run: stages,
 * Back presses, screenshots) and marked posted. */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ifMatch, mediaUrl, type Schemas, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { DownloadVideo } from "../../components/DownloadVideo";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { useAction } from "../lib/actions";
import { addDays, budapestTime, fmtWhen, isDay, isTime, todayBudapest, weekDays, weekdayName } from "../lib/time";
import { ActionError, ConfirmButton, Empty, QueryView, Section, TableScroll } from "../lib/ui";
import { type Patch, useUrlState } from "../lib/url";
import { cellKey, lanes, parseCalParams, postSlot, postsByCell, postWeek, quotaFor, slotUnix, weekLabel, weekRange } from "./model";

type Post = Schemas["PostOut"];
type PostRun = Schemas["PostRunOut"];
type Render = Schemas["RenderOut"];
type Calendar = Schemas["CalendarOut"];
type Check = Schemas["CheckOut"];
type Slot = { account: string; day: string; time: string };

const CAL_KEY = qk.list("calendar");

function slotText(s: Slot): string {
  return `${s.account} ${s.day} ${s.time}`;
}

export function Violations({ check }: { check: Check | null }) {
  if (!check) return null;
  if (check.ok && check.violations.length === 0) {
    return <p className="ws7-tone-ok" data-testid="check-ok">Slot OK: no rule is violated.</p>;
  }
  return (
    <div role="alert" data-testid="violations">
      <strong>{check.violations.length} violation{check.violations.length === 1 ? "" : "s"}:</strong>
      <ul className="ws7-violations" aria-label="Violations">
        {check.violations.map((v, i) => (
          <li key={`${v.code}-${i}`}><code>{v.code}</code>: {v.message}</li>
        ))}
      </ul>
    </div>
  );
}

function SlotFields({ slot, onChange, accounts, prefix }: { slot: Partial<Slot>; onChange: (s: Partial<Slot>) => void; accounts: string[]; prefix: string }) {
  return (
    <div className="ws7-toolbar">
      <label className="ws7-field">
        Account
        <select value={slot.account ?? ""} data-testid={`${prefix}-account`} onChange={(e) => onChange({ ...slot, account: e.target.value || undefined })}>
          <option value="">Choose…</option>
          {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </label>
      <label className="ws7-field">
        Day
        <input type="date" value={slot.day ?? ""} data-testid={`${prefix}-day`} onChange={(e) => onChange({ ...slot, day: e.target.value || undefined })} />
      </label>
      <label className="ws7-field">
        Time (Budapest)
        <input type="time" value={slot.time ?? ""} data-testid={`${prefix}-time`} onChange={(e) => onChange({ ...slot, time: e.target.value || undefined })} />
      </label>
    </div>
  );
}

function completeSlot(s: Partial<Slot>): Slot | null {
  return s.account && isDay(s.day) && isTime(s.time) ? { account: s.account, day: s.day, time: s.time } : null;
}

function useCheck() {
  return useAction(
    (renderId: number, s: Slot) => unwrap(api.POST("/api/posts/check", { body: { render_id: renderId, account_id: s.account, t_post: slotUnix(s.day, s.time) } })),
    {
      label: (renderId) => `Check slot for render #${renderId}`,
      success: (r, renderId, s) => (r.ok && r.violations.length === 0
        ? `Slot OK for render #${renderId}: ${slotText(s)}`
        : `Render #${renderId} at ${slotText(s)}: ${r.violations.length} violation(s): ${r.violations.map((v) => v.code).join(", ")}`),
    },
  );
}

function PlaceForm({ render, accounts, slot, setSlot, onDone }: {
  render: Render;
  accounts: string[];
  slot: Partial<Slot>;
  setSlot: (s: Partial<Slot>) => void;
  onDone: () => void;
}) {
  const [check, setCheck] = useState<Check | null>(null);
  const [caption, setCaption] = useState("");
  const over = caption.length > CAPTION_MAX;
  const checker = useCheck();
  const schedule = useAction(
    (s: Slot) => unwrap(api.POST("/api/posts", { body: { render_id: render.id, account_id: s.account, t_post: slotUnix(s.day, s.time),
      caption: caption.trim() ? caption : null } })),
    { label: `Schedule render #${render.id}`, success: (p, s) => `Scheduled render #${render.id} on ${s.account} at ${s.day} ${s.time} (post #${p.id})`, invalidate: [CAL_KEY] },
  );
  const full = completeSlot(slot);
  useEffect(() => setCheck(null), [slot.account, slot.day, slot.time]);
  return (
    <form className="ws7-form" aria-label={`Place render #${render.id}`} data-testid="place-form" onSubmit={(e) => e.preventDefault()}>
      <p><strong>Place render #{render.id}</strong> ({render.template_id}, clip #{render.clip_id})</p>
      <SlotFields slot={slot} onChange={setSlot} accounts={accounts} prefix="place" />
      <label className="ws7-field">
        Description (TikTok caption)
        <textarea rows={3} value={caption} data-testid="place-caption" placeholder="Empty = the default caption ({song} · {artists})"
          onChange={(e) => setCaption(e.target.value)} />
      </label>
      <div className={over ? "ws7-tone-bad" : "ws7-muted"} data-testid="place-caption-count">
        {caption.length}/{CAPTION_MAX}{caption.trim() ? "" : " · empty = default caption, editable later on the post"}
      </div>
      <div className="ws7-row">
        <button type="button" data-testid="place-check" disabled={!full || checker.pending}
          onClick={async () => { if (full) setCheck((await checker.run(render.id, full)) ?? null); }}>
          Check
        </button>
        <button type="button" className="ws7-primary" data-testid="place-schedule" disabled={!full || schedule.pending || over}
          onClick={async () => { if (full && (await schedule.run(full))) onDone(); }}>
          Schedule
        </button>
        <button type="button" data-testid="place-cancel" onClick={onDone}>Cancel</button>
      </div>
      {!full && <p className="ws7-muted">Choose an account, a day and a time.</p>}
      <Violations check={check} />
      <ActionError error={checker.error ?? schedule.error} />
    </form>
  );
}

function MoveForm({ post, accounts, onDone }: { post: Post; accounts: string[]; onDone: () => void }) {
  const qc = useQueryClient();
  const [slot, setSlot] = useState<Partial<Slot>>(postSlot(post));
  const [check, setCheck] = useState<Check | null>(null);
  const checker = useCheck();
  const move = useAction(
    (s: Slot) => unwrap(api.PATCH("/api/posts/{post_id}", { params: { path: { post_id: post.id } }, body: { account_id: s.account, t_post: slotUnix(s.day, s.time) }, headers: ifMatch(post.version) })),
    { label: `Move post #${post.id}`, success: (_p, s) => `Moved post #${post.id} to ${slotText(s)}`, invalidate: [CAL_KEY] },
  );
  const full = completeSlot(slot);
  return (
    <form className="ws7-form" aria-label={`Move post #${post.id}`} data-testid={`move-form-${post.id}`} onSubmit={(e) => e.preventDefault()}>
      <SlotFields slot={slot} onChange={(s) => { setSlot(s); setCheck(null); }} accounts={accounts} prefix={`move-${post.id}`} />
      <div className="ws7-row">
        <button type="button" data-testid={`move-check-${post.id}`} disabled={!full || checker.pending}
          onClick={async () => { if (full) setCheck((await checker.run(post.render_id, full)) ?? null); }}>
          Check
        </button>
        <button type="button" className="ws7-primary" data-testid={`move-save-${post.id}`} disabled={!full || move.pending}
          onClick={async () => { if (full && (await move.run(full))) onDone(); }}>
          Save move
        </button>
        <button type="button" data-testid={`move-cancel-${post.id}`} onClick={onDone}>Cancel</button>
      </div>
      <Violations check={check} />
      <ActionError error={checker.error ?? move.error} onReload={async () => { await qc.invalidateQueries({ queryKey: CAL_KEY }); move.clear(); onDone(); }} />
    </form>
  );
}

function MarkPostedForm({ post, onDone }: { post: Post; onDone: () => void }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(post.url ?? "");
  const [item, setItem] = useState(post.tiktok_item_id ?? "");
  const mark = useAction(
    () => unwrap(api.POST("/api/posts/{post_id}/mark-posted", { params: { path: { post_id: post.id } }, body: { url: url.trim() || null, tiktok_item_id: item.trim() || null }, headers: ifMatch(post.version) })),
    { label: `Mark post #${post.id} posted`, success: (p) => `Marked post #${p.id} posted${p.url ? ` (${p.url})` : ""}`, invalidate: [CAL_KEY] },
  );
  return (
    <form className="ws7-form" aria-label={`Mark post #${post.id} posted`} data-testid={`posted-form-${post.id}`}
      onSubmit={async (e) => { e.preventDefault(); if (await mark.run()) onDone(); }}>
      <label className="ws7-field">
        TikTok URL
        <input type="url" value={url} data-testid={`posted-url-${post.id}`} onChange={(e) => setUrl(e.target.value)} />
      </label>
      <label className="ws7-field">
        TikTok item id
        <input value={item} inputMode="numeric" data-testid={`posted-item-${post.id}`} onChange={(e) => setItem(e.target.value)} />
      </label>
      <div className="ws7-row">
        <button type="submit" className="ws7-primary" data-testid={`posted-save-${post.id}`} disabled={mark.pending || (!url.trim() && !item.trim())}>
          Save posted
        </button>
        <button type="button" data-testid={`posted-cancel-${post.id}`} onClick={onDone}>Cancel</button>
      </div>
      <ActionError error={mark.error} onReload={async () => { await qc.invalidateQueries({ queryKey: CAL_KEY }); mark.clear(); onDone(); }} />
    </form>
  );
}

const CAPTION_MAX = 2200;

/** The TikTok description sent with the GeeLark task. Empty = the `dist.caption_template` default. */
function CaptionForm({ post, onDone }: { post: Post; onDone: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState(post.caption ?? "");
  const save = useAction(
    () => unwrap(api.PATCH("/api/posts/{post_id}", { params: { path: { post_id: post.id } }, body: { caption: text }, headers: ifMatch(post.version) })),
    { label: `Save caption of post #${post.id}`, success: (p) => `Caption of post #${p.id} saved${p.caption ? "" : " (template default)"}`, invalidate: [CAL_KEY] },
  );
  const over = text.length > CAPTION_MAX;
  return (
    <form className="ws7-form" aria-label={`Caption of post #${post.id}`} data-testid={`caption-form-${post.id}`}
      onSubmit={async (e) => { e.preventDefault(); if (await save.run()) onDone(); }}>
      <label className="ws7-field">
        TikTok description
        <textarea rows={4} value={text} data-testid={`caption-text-${post.id}`} placeholder={post.caption_default ?? ""}
          onChange={(e) => setText(e.target.value)} />
      </label>
      <div className={over ? "ws7-tone-bad" : "ws7-muted"} data-testid={`caption-count-${post.id}`}>
        {text.length}/{CAPTION_MAX}{text.trim() ? "" : ` · empty = default: ${post.caption_default ?? ""}`}
      </div>
      <div className="ws7-row">
        <button type="submit" className="ws7-primary" data-testid={`caption-save-${post.id}`} disabled={save.pending || over}>Save caption</button>
        <button type="button" data-testid={`caption-cancel-${post.id}`} onClick={onDone}>Cancel</button>
      </div>
      <ActionError error={save.error} onReload={async () => { await qc.invalidateQueries({ queryKey: CAL_KEY }); save.clear(); onDone(); }} />
    </form>
  );
}

/** The watched GeeLark publish task(s): stage timeline, Back presses on the Play sheet, gallery check, screenshots. */
function RunPanel({ post }: { post: Post }) {
  const live = post.status === "posting" || post.status === "ordered";
  const runs = useQuery({
    queryKey: [...CAL_KEY, "runs", post.id],
    queryFn: () => unwrap(api.GET("/api/posts/{post_id}/runs", { params: { path: { post_id: post.id } } })),
    refetchInterval: live ? 5000 : false,
  });
  return (
    <div className="ws7-run" data-testid={`runs-${post.id}`}>
      <QueryView q={runs} what="the publish runs">
        {(rs: PostRun[]) => rs.length === 0 ? <Empty>No watched GeeLark task yet{live ? " (starts when GeeLark accepts the order)" : ""}.</Empty> : (
          <>{rs.map((r) => (
            <div key={r.id} data-testid={`run-${r.id}`}>
              <div className="ws7-row">
                <strong>Task {r.task_id}</strong>
                <StatusPill state={r.final === "DONE" ? "done" : r.final === "running" ? "running" : "failed"} label={r.final} />
                <span>Back presses: {r.back_presses}</span>
                {r.share_link && <a href={r.share_link} target="_blank" rel="noreferrer">share link</a>}
              </div>
              {(r.fail_code || r.fail_desc || r.error) && (
                <div className="ws7-tone-bad">{r.fail_code ? `${r.fail_code} ` : ""}{r.fail_desc ?? ""}{r.error ? ` ${r.error}` : ""}</div>
              )}
              {r.stray_media.length > 0 && (
                <div className="ws7-tone-bad" role="alert" data-testid={`run-stray-${r.id}`}>
                  Non-video file on the phone during the task (GeeAuto may have posted it): {r.stray_media.join(", ")}
                </div>
              )}
              <TableScroll label={`Stages of task ${r.task_id}`}>
                <table className="ws7-table">
                  <thead><tr><th>Stage</th><th>Start</th><th>Seconds</th></tr></thead>
                  <tbody>
                    {r.stages.map((st, i) => (
                      <tr key={i}><td><code>{String(st.stage)}</code></td><td>{budapestTime(Math.round(Number(st.start)))}</td><td>{String(st.seconds)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
              <div className="ws7-muted">Gallery during the task: {r.new_media.length ? r.new_media.join(", ") : "nothing new"}</div>
              <ul className="ws7-shots" aria-label={`Screenshots of task ${r.task_id}`}>
                {r.shots.map((sh) => (
                  <li key={sh.media_id}>
                    <a href={mediaUrl(sh.media_id)} target="_blank" rel="noreferrer">
                      <img src={mediaUrl(sh.media_id)} alt={`Phone screen at ${sh.stage}`} loading="lazy" width={90} />
                    </a>
                    <span className="ws7-muted">{sh.stage}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}</>
        )}
      </QueryView>
    </div>
  );
}

function PostCard({ post, accounts, autopost, moving, marking, highlight, set }: {
  post: Post;
  accounts: string[];
  /** The post's account may be ordered automatically (AccountOut.autopost). */
  autopost: boolean;
  moving: boolean;
  marking: boolean;
  /** Opened from a `/calendar?post=<id>` link: marked and scrolled into view. */
  highlight: boolean;
  set: (p: Patch) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (highlight) ref.current?.scrollIntoView?.({ block: "center" });
  }, [highlight]);
  const qc = useQueryClient();
  const reload = async () => { await qc.invalidateQueries({ queryKey: CAL_KEY }); };
  const unschedule = useAction(
    () => unwrap(api.DELETE("/api/posts/{post_id}", { params: { path: { post_id: post.id } }, headers: ifMatch(post.version) })),
    { label: `Unschedule post #${post.id}`, success: () => `Unscheduled post #${post.id} (render #${post.render_id} is back in the list)`, invalidate: [CAL_KEY] },
  );
  const order = useAction(
    () => unwrap(api.POST("/api/posts/{post_id}/order", { params: { path: { post_id: post.id } }, headers: ifMatch(post.version) })),
    { label: `Order post #${post.id}`, success: (p) => `Ordered post #${p.id} on GeeLark (${p.status})`, invalidate: [CAL_KEY] },
  );
  const planned = post.status === "planned";
  const name = `post #${post.id}`;
  const [captioning, setCaptioning] = useState(false);
  const [showRun, setShowRun] = useState(post.status === "posting");
  const hasRun = post.status !== "planned" && post.status !== "skipped";
  // An ordered post that GeeLark has not accepted yet can still be unscheduled (its queued publish job is cancelled).
  const canUnschedule = planned || post.status === "failed" || (post.status === "ordered" && !post.geelark_task_id);
  return (
    <div ref={ref} className={`ws7-post${highlight ? " ws7-highlight" : ""}`} data-testid={`post-${post.id}`}
      data-highlight={highlight ? "true" : undefined}>
      <div>
        <strong>{budapestTime(post.t_post)}</strong> {post.template_id ?? "render"}{" "}
        <Link to={`/renders/${post.render_id}`}>render #{post.render_id}</Link>
      </div>
      <div className="ws7-row">
        <StatusPill state={post.status === "posted" ? "done" : post.status === "failed" ? "failed" : post.status === "planned" ? "pending" : post.status === "skipped" ? "skipped" : "running"} label={post.status} />
        {post.url && <a href={post.url} target="_blank" rel="noreferrer">TikTok</a>}
      </div>
      {post.error && <div className="ws7-tone-bad">{post.error}</div>}
      {post.media_suspect && (
        <div className="ws7-tone-bad" role="alert" data-testid={`post-suspect-${post.id}`}>
          Media check: a non-video file reached the phone during the task. Open the post on TikTok and verify it shows the video.
        </div>
      )}
      <div className="ws7-muted ws7-caption" data-testid={`post-caption-${post.id}`} title="TikTok description">
        {post.caption ?? post.caption_default ?? ""}{post.caption ? "" : " (default)"}
      </div>
      <div className="ws7-row">
        {planned && (
          <button type="button" data-testid={`post-caption-edit-${post.id}`} aria-label={`Edit the TikTok description of ${name}`} aria-expanded={captioning}
            onClick={() => setCaptioning(!captioning)}>
            Caption
          </button>
        )}
        {hasRun && (
          <button type="button" data-testid={`post-run-${post.id}`} aria-label={`Publish run of ${name}`} aria-expanded={showRun}
            onClick={() => setShowRun(!showRun)}>
            Run
          </button>
        )}
        {planned && (
          <button type="button" data-testid={`post-move-${post.id}`} aria-label={`Move ${name}`} aria-expanded={moving}
            onClick={() => set({ move: moving ? undefined : post.id, posted: undefined })}>
            Move
          </button>
        )}
        {canUnschedule && (
          <ConfirmButton label="Unschedule" ariaLabel={`Unschedule ${name}`} confirmLabel={`Confirm unschedule ${name}`} testId={`post-unschedule-${post.id}`}
            disabled={unschedule.pending} onConfirm={() => void unschedule.run()} />
        )}
        {planned && autopost && (
          <button type="button" className="ws7-primary" data-testid={`post-order-${post.id}`} aria-label={`Order posting of ${name}`}
            disabled={order.pending} onClick={() => void order.run()}>
            Order posting
          </button>
        )}
        {post.status !== "posted" && post.status !== "skipped" && (
          <button type="button" data-testid={`post-mark-${post.id}`} aria-label={`Mark posted: ${name}`} aria-expanded={marking}
            onClick={() => set({ posted: marking ? undefined : post.id, move: undefined })}>
            Mark posted
          </button>
        )}
        <DownloadVideo mediaId={post.output_media_id} label={`Download video of ${name}`} />
      </div>
      {planned && !autopost && (
        <div className="ws7-muted" data-testid={`post-autopost-off-${post.id}`}>
          automated posting is off for {post.account_id} (Settings → distribution → dist.autopost_accounts)
        </div>
      )}
      <ActionError error={unschedule.error ?? order.error} onReload={() => void reload()} />
      {captioning && <CaptionForm post={post} onDone={() => setCaptioning(false)} />}
      {showRun && hasRun && <RunPanel post={post} />}
      {moving && <MoveForm post={post} accounts={accounts} onDone={() => set({ move: undefined })} />}
      {marking && <MarkPostedForm post={post} onDone={() => set({ posted: undefined })} />}
    </div>
  );
}

function RenderCard({ r, selected, onSelect }: { r: Render; selected: boolean; onSelect: () => void }) {
  return (
    <li className={`ws7-render-card${selected ? " selected" : ""}`} data-testid={`unscheduled-${r.id}`}>
      <div><strong>Render #{r.id}</strong> · {r.template_id} · clip #{r.clip_id}</div>
      {r.sheet_media_id != null && <img src={mediaUrl(r.sheet_media_id)} alt={`Contact sheet of render #${r.id}`} loading="lazy" />}
      <div className="ws7-row">
        <button type="button" className={selected ? undefined : "ws7-primary"} data-testid={`select-render-${r.id}`} aria-pressed={selected}
          aria-label={`${selected ? "Deselect" : "Select"} render #${r.id} to place`} onClick={onSelect}>
          {selected ? "Deselect" : "Select"}
        </button>
        <Link className="button" to={`/renders/${r.id}`} aria-label={`Preview render #${r.id}`}>Preview</Link>
        <DownloadVideo mediaId={r.output_media_id} label={`Download video of render #${r.id}`} />
      </div>
      <div className="ws7-muted">finished {fmtWhen(r.finished_at)}{r.qa_actor ? ` · approved by ${r.qa_actor}` : ""}</div>
    </li>
  );
}

function WeekGrid({ cal, days, selected, target, set }: {
  cal: Calendar;
  days: string[];
  selected: Render | undefined;
  target: { account?: string; day?: string };
  set: (p: Patch, msg?: string) => void;
}) {
  const { sp } = useUrlState();
  const p = parseCalParams(sp);
  const byCell = postsByCell(cal.posts);
  const accountIds = lanes(cal.accounts);
  const today = todayBudapest();
  return (
    <TableScroll label="Week grid">
      <table className="ws7-cal-grid" aria-label={`Posts ${weekLabel(days[0] ?? "")}`} data-testid="week-grid">
        <thead>
          <tr>
            <th scope="col">Account</th>
            {days.map((d) => (
              <th key={d} scope="col" className={d === today ? "ws7-today" : undefined}>{weekdayName(d)} {d.slice(5)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {accountIds.map((a) => {
            const acc = cal.accounts.find((x) => x.id === a);
            return (
              <tr key={a}>
                <th scope="row">
                  {a}
                  <div className="ws7-muted">{acc?.handle ?? ""}{acc && !acc.enabled ? " (disabled)" : ""}</div>
                </th>
                {days.map((d) => {
                  const posts = byCell.get(cellKey(a, d)) ?? [];
                  const q = quotaFor(cal.quota, cal.accounts, posts, a, d);
                  const isTarget = target.account === a && target.day === d;
                  return (
                    <td key={d} data-testid={`cell-${a}-${d}`} className={[d === today ? "ws7-today" : "", isTarget ? "ws7-target" : ""].join(" ").trim() || undefined}>
                      <div className={`ws7-quota${q.used >= q.quota ? " full" : ""}`} data-testid={`quota-${a}-${d}`}>{q.used}/{q.quota} posts</div>
                      {posts.map((post) => (
                        <PostCard key={post.id} post={post} accounts={accountIds} autopost={acc?.autopost === true}
                          moving={p.move === post.id} marking={p.posted === post.id} highlight={p.post === post.id}
                          set={(patch) => set(patch)} />
                      ))}
                      {selected && (
                        <button type="button" data-testid={`place-here-${a}-${d}`} aria-label={`Place here: render #${selected.id} on ${a} ${d}`}
                          onClick={() => set({ account: a, day: d }, `Placing render #${selected.id} on ${a} ${d}; choose a time, then Check`)}>
                          Place here
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableScroll>
  );
}

export function CalendarView() {
  const announce = useAnnounce();
  const { sp, set: setUrl } = useUrlState();
  const p = parseCalParams(sp);
  const days = weekDays(p.week);
  const range = weekRange(p.week);
  const cal = useQuery({
    queryKey: qk.list("calendar", range),
    queryFn: () => unwrap(api.GET("/api/calendar", { params: { query: { from: range.from, to: range.to } } })),
    placeholderData: keepPreviousData,
  });
  const set = (patch: Patch, msg?: string) => {
    setUrl(patch);
    if (msg) announce(msg);
  };
  // `/calendar?post=<id>` (notifications, jobs): load the post once, jump to the Budapest week of its t_post.
  const focus = useQuery({
    queryKey: qk.entity("post", p.post ?? 0),
    queryFn: () => unwrap(api.GET("/api/posts/{post_id}", { params: { path: { post_id: p.post ?? 0 } } })),
    enabled: p.post != null,
  });
  const jumped = useRef<number | null>(null);
  useEffect(() => {
    const fp = focus.data;
    if (!fp || fp.id !== p.post || jumped.current === fp.id) return;
    jumped.current = fp.id;
    const w = postWeek(fp.t_post);
    if (w !== p.week) setUrl({ week: w }, { replace: true });
    announce(`Showing post #${fp.id} (${fp.account_id}, ${fmtWhen(fp.t_post)})`);
  }, [focus.data, p.post, p.week, setUrl, announce]);
  const unscheduled = cal.data?.approved_unscheduled ?? [];
  const selected = unscheduled.find((r) => r.id === p.render);
  const accountIds = lanes(cal.data?.accounts ?? []);
  return (
    <div className="ws7-page" data-testid="calendar-page">
      <PageHeader title="Calendar" md={{ page: "calendar", params: { from: range.from, to: range.to } }}>
        <span className="ws7-inline" role="group" aria-label="Week navigation">
          <button type="button" data-testid="week-prev" onClick={() => set({ week: addDays(p.week, -7) }, `Week of ${addDays(p.week, -7)}`)}>← Previous week</button>
          <button type="button" data-testid="week-today" onClick={() => set({ week: undefined }, "This week")}>This week</button>
          <button type="button" data-testid="week-next" onClick={() => set({ week: addDays(p.week, 7) }, `Week of ${addDays(p.week, 7)}`)}>Next week →</button>
        </span>
      </PageHeader>
      <p className="ws7-muted" data-testid="week-label">Week {weekLabel(p.week)} (Europe/Budapest)</p>
      {p.post != null && focus.isError && (
        <p className="ws7-tone-bad" role="alert" data-testid="post-focus-error">Post #{p.post} could not be loaded.</p>
      )}
      <QueryView q={cal} what="the calendar">
        {(c) => (
          <div className="ws7-cal">
            <Section id="unscheduled" title={`Approved, not scheduled (${c.approved_unscheduled.length})`}>
              {selected && (
                <PlaceForm render={selected} accounts={accountIds}
                  slot={{ account: p.account, day: p.day ?? (days.includes(todayBudapest()) ? todayBudapest() : p.week), time: p.time }}
                  setSlot={(s) => setUrl({ account: s.account, day: s.day, time: s.time }, { replace: true })}
                  onDone={() => setUrl({ render: undefined, account: undefined, day: undefined, time: undefined })} />
              )}
              {c.approved_unscheduled.length === 0 ? (
                <Empty>No approved renders waiting for a slot.</Empty>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: 0 }} aria-label="Approved unscheduled renders">
                  {c.approved_unscheduled.map((r) => (
                    <RenderCard key={r.id} r={r} selected={r.id === p.render}
                      onSelect={() => (r.id === p.render
                        ? set({ render: undefined }, `Deselected render #${r.id}`)
                        : set({ render: r.id }, `Selected render #${r.id}; choose account, day and time`))} />
                  ))}
                </ul>
              )}
            </Section>
            <Section id="week" title="Week">
              <WeekGrid cal={c} days={days} selected={selected} target={{ account: p.account, day: p.day }} set={set} />
            </Section>
          </div>
        )}
      </QueryView>
    </div>
  );
}
